import React from 'react';
import { FileTextIcon } from './icons/FileTextIcon';

import type { PdfAnalysisResult } from '../services/fileParser';

export interface FileProcessingStatus {
  file: File;
  status: 'pending' | 'processing' | 'success' | 'error';
  progress?: string;
  error?: string;
  result?: string;
  autoName?: string;
  studentToken?: string;
  sentryEventId?: string;
  pdfAnalysis?: PdfAnalysisResult;
  ocrPageFailures?: number[];
  pageCount?: number;
}

interface BatchProcessingViewProps {
  files: FileProcessingStatus[];
  currentIndex: number;
}

const BatchProcessingView: React.FC<BatchProcessingViewProps> = ({ files, currentIndex }) => {
  const completedCount = files.filter(f => f.status === 'success').length;
  const errorCount = files.filter(f => f.status === 'error').length;
  const totalProgress = ((completedCount + errorCount) / files.length) * 100;

  return (
    <div className="w-full p-8 bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-2">
          Processing {files.length} Files
        </h2>
        <p className="text-slate-500 dark:text-slate-400">
          {completedCount} of {files.length} completed
          {errorCount > 0 && <span className="text-red-500"> ({errorCount} failed)</span>}
        </p>
      </div>

      {/* Overall Progress Bar */}
      <div className="mb-6">
        <div className="h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
          <div 
            className="h-full bg-emerald-500 transition-all duration-500 ease-out"
            style={{ width: `${totalProgress}%` }}
          />
        </div>
      </div>

      {/* File List */}
      <div className="space-y-3">
        {files.map((item, index) => (
          <div 
            key={index}
            className={`flex items-center gap-4 p-4 rounded-lg border transition-all ${
              item.status === 'processing' 
                ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-700'
                : item.status === 'success'
                  ? 'bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-700'
                  : item.status === 'error'
                    ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700'
                    : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 opacity-60'
            }`}
          >
            {/* Status Icon */}
            <div className="flex-shrink-0">
              {item.status === 'pending' && (
                <div className="w-8 h-8 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center">
                  <span className="text-sm font-medium text-slate-500 dark:text-slate-400">{index + 1}</span>
                </div>
              )}
              {item.status === 'processing' && (
                <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center">
                  <svg className="w-5 h-5 text-emerald-600 dark:text-emerald-400 animate-spin" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                </div>
              )}
              {item.status === 'success' && (
                <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center">
                  <svg className="w-5 h-5 text-emerald-600 dark:text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              )}
              {item.status === 'error' && (
                <div className="w-8 h-8 rounded-full bg-red-100 dark:bg-red-900/50 flex items-center justify-center">
                  <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
              )}
            </div>

            {/* File Info */}
            <div className="flex-grow min-w-0">
              <div className="flex items-center gap-2">
                <FileTextIcon className="w-4 h-4 text-slate-400 flex-shrink-0" />
                <p className="text-sm font-medium text-slate-700 dark:text-slate-200 truncate">
                  {item.file.name}
                </p>
              </div>
              {item.status === 'processing' && item.progress && (
                <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">
                  {item.progress}
                </p>
              )}
              {item.status === 'error' && item.error && (
                <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                  {item.error}
                </p>
              )}
            </div>

            {/* Status Label */}
            <div className="flex-shrink-0">
              {item.status === 'pending' && (
                <span className="text-xs text-slate-400 dark:text-slate-500">Waiting</span>
              )}
              {item.status === 'processing' && (
                <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">Processing...</span>
              )}
              {item.status === 'success' && (
                <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">Done</span>
              )}
              {item.status === 'error' && (
                <span className="text-xs text-red-600 dark:text-red-400 font-medium">Failed</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BatchProcessingView;
